import React, { useState } from 'react';
import { HelpCircle, X, BookOpen, Lightbulb, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';

// Page explanation content for all pages
const pageExplanations = {
  dashboard: {
    title: "Dashboard Overview",
    description: "The Dashboard provides a real-time overview of your verification operations, showing key metrics and recent activity at a glance.",
    sections: [
      {
        title: "Key Metrics",
        content: "At the top, you'll see summary cards showing:\n• **Total Cases**: All verification cases in the system\n• **Pending Cases**: Cases awaiting review\n• **Approved Today**: Successfully verified cases\n• **Average Processing Time**: How quickly cases are being handled"
      },
      {
        title: "Case Distribution Chart",
        content: "The pie chart shows how cases are distributed across different statuses (pending, approved, rejected, etc.), helping you understand your workload at a glance."
      },
      {
        title: "Recent Activity",
        content: "The activity feed shows the latest actions taken by your team, including:\n• Case approvals and rejections\n• New case assignments\n• Status changes\n\nClick on any activity item to view the related case."
      },
      {
        title: "SLA Compliance",
        content: "Monitor your Service Level Agreement compliance. Cases approaching or past their deadline are highlighted so you can prioritize accordingly."
      }
    ],
    tips: [
      "Check the dashboard daily to stay on top of your verification workload",
      "Use the pending cases metric to gauge team capacity",
      "Monitor SLA compliance to avoid overdue cases"
    ]
  },
  
  caseQueue: {
    title: "Case Queue Management",
    description: "The Case Queue is your primary workspace for managing verification cases. Here you can view, filter, sort, and assign cases to team members.",
    sections: [
      {
        title: "Filtering Cases",
        content: "Use the filters at the top to narrow down cases:\n• **Status**: Filter by pending, approved, rejected, etc.\n• **Priority**: Show only high, normal, or low priority cases\n• **Assigned To**: View cases assigned to specific team members\n• **Date Range**: Filter by when cases were created"
      },
      {
        title: "Case List",
        content: "Each row shows key information:\n• **Applicant Name**: The person being verified\n• **Status Badge**: Current case status with color coding\n• **Risk Score**: AI-calculated risk level (0-100)\n• **SLA Deadline**: When the case must be completed\n• **Assigned Agent**: Who is handling the case"
      },
      {
        title: "Sorting",
        content: "Click any column header to sort by that field. Click again to reverse the sort order. Common sorting strategies:\n• Sort by deadline to prioritize urgent cases\n• Sort by risk score to handle high-risk cases first"
      },
      {
        title: "Bulk Actions",
        content: "Select multiple cases using the checkboxes to:\n• Assign them to an agent in bulk\n• Change priority for multiple cases\n• Export selected cases"
      }
    ],
    tips: [
      "Sort by SLA deadline to avoid missing deadlines",
      "Use the risk score to prioritize potentially fraudulent cases",
      "Regularly check for unassigned cases"
    ]
  },
  
  caseDetail: {
    title: "Case Detail & Review",
    description: "This is where you perform the actual verification review. Examine evidence, check AI analysis results, and make your decision.",
    sections: [
      {
        title: "Evidence Viewer (Left Panel)",
        content: "The left panel displays uploaded documents and selfies:\n• **Zoom & Pan**: Use the controls to examine documents closely\n• **Rotate**: Rotate images if they're oriented incorrectly\n• **Brightness**: Adjust brightness to see faded documents\n• **Thumbnails**: Click thumbnails at the bottom to switch between evidence items"
      },
      {
        title: "Details Tab",
        content: "Shows applicant information and case metadata:\n• Contact details (email, phone)\n• Case origin channel\n• Priority level\n• Creation date and SLA deadline\n• Extracted document fields (from OCR)"
      },
      {
        title: "Risk Analysis Tab",
        content: "Displays AI-powered analysis results:\n• **Verification Confidence**: Overall score (0-100%)\n• **Face Comparison**: Match score between ID photo and selfie\n• **Document Validity**: Checks for expiration, tampering\n• **MRZ Verification**: Machine-readable zone validation\n• **Data Validation**: Compares user-entered data with OCR results\n• **Liveness Check**: Verifies selfie authenticity"
      },
      {
        title: "History Tab",
        content: "Shows the complete audit trail for this case:\n• Who viewed or modified the case\n• Status changes with timestamps\n• Messages sent to the applicant\n• Evidence uploads and deletions"
      },
      {
        title: "Making a Decision",
        content: "Click 'Make Decision' to:\n• **Approve**: Verify the applicant's identity\n• **Reject**: Deny verification with reason codes\n• **Request More Info**: Ask applicant for additional documents\n• **Escalate**: Send to a supervisor for review\n• **Flag as Fraud**: Mark as suspected fraud"
      }
    ],
    tips: [
      "Always check the face match score before approving",
      "Look for discrepancies in the Data Validation section",
      "Use the History tab to see if others have reviewed this case",
      "Run AI Analysis if it hasn't been done yet"
    ]
  },
  
  invitations: {
    title: "Invitation Management",
    description: "Create and manage verification invitations. Invitations are sent to candidates to start their identity verification process.",
    sections: [
      {
        title: "Creating Invitations",
        content: "Click 'New Invitation' to create a verification request:\n• **Name**: Candidate's full name\n• **Email**: Where to send the invitation\n• **Phone**: For SMS notifications (optional)\n• **Country**: Their country for document type selection\n• **Priority**: Set urgency level\n• **Callback URL**: Webhook URL to receive results (for API integrations)"
      },
      {
        title: "Invitation Statuses",
        content: "Invitations go through these stages:\n• **Pending**: Invitation sent, awaiting candidate action\n• **In Progress**: Candidate has started the verification\n• **Completed**: Verification submitted for review\n• **Expired**: Invitation link has expired"
      },
      {
        title: "Resending Invitations",
        content: "If a candidate hasn't responded, you can:\n• Resend the email with the original link\n• Copy the verification link to share manually\n• Extend the expiration date"
      },
      {
        title: "Webhook Integration",
        content: "For automated systems, provide a callback URL. When verification is complete, results will be POSTed to your URL with:\n• Case status (approved/rejected)\n• Risk scores\n• Extracted data from documents"
      }
    ],
    tips: [
      "Set appropriate priority based on business needs",
      "Use callback URLs for system-to-system integration",
      "Monitor pending invitations and follow up if needed"
    ]
  },
  
  templates: {
    title: "Email Template Management",
    description: "Manage and customize email templates used throughout the system. All automated emails can be customized to match your brand voice.",
    sections: [
      {
        title: "Available Templates",
        content: "The system uses templates for:\n• **Invitation**: Sent when creating a new verification request\n• **Reminder**: Follow-up for pending invitations\n• **Approved**: Notification when verification succeeds\n• **Rejected**: Notification when verification is denied\n• **More Info Needed**: Request for additional documents\n• **Under Review**: Status update during manual review"
      },
      {
        title: "Editing Templates",
        content: "Each template has:\n• **Subject**: The email subject line\n• **Body**: Main content with variable placeholders\n\nUse variables like {{applicant_name}}, {{case_id}}, {{verification_link}} that get replaced with actual values when sent."
      },
      {
        title: "Available Variables",
        content: "Common variables you can use:\n• {{applicant_name}} - The candidate's name\n• {{case_id}} - Unique case reference\n• {{verification_link}} - Link to start verification\n• {{deadline}} - SLA deadline date\n• {{company_name}} - Your organization name"
      },
      {
        title: "Export & Import",
        content: "Use export/import to:\n• Back up your templates\n• Transfer templates between environments (test → production)\n• Share templates with other administrators\n\nExported templates are saved as JSON files."
      }
    ],
    tips: [
      "Always preview templates before saving changes",
      "Keep templates professional and clear",
      "Test templates with real data before going live",
      "Export templates before making major changes as backup"
    ]
  },
  
  users: {
    title: "User Management",
    description: "Manage team members who have access to the EdoVerify system. Control roles, permissions, and account status.",
    sections: [
      {
        title: "User Roles",
        content: "Each user has one role that determines their permissions:\n• **Admin**: Full system access, user management, settings\n• **Supervisor**: Case management, team oversight, templates\n• **Verification Agent**: Review and decide on cases\n• **Fraud Analyst**: Focus on high-risk and fraud cases\n• **Auditor**: Read-only access for compliance review"
      },
      {
        title: "Creating Users",
        content: "Click 'Add User' to create a new account:\n• Enter their email address\n• Set a temporary password\n• Assign appropriate role\n• User will receive login credentials"
      },
      {
        title: "Managing Users",
        content: "For existing users, you can:\n• **Change Role**: Promote or adjust access level\n• **Reset Password**: Generate new credentials\n• **Deactivate**: Disable account without deleting\n• **Delete**: Permanently remove user"
      },
      {
        title: "Impersonation (Admin Only)",
        content: "Admins can 'impersonate' other users to:\n• See the system from their perspective\n• Troubleshoot access issues\n• Verify role permissions\n\nImpersonation is read-only and fully logged for audit purposes."
      }
    ],
    tips: [
      "Follow principle of least privilege when assigning roles",
      "Regularly review user accounts and remove inactive ones",
      "Use impersonation to verify new users have correct access"
    ]
  },
  
  auditTrail: {
    title: "Audit Trail",
    description: "The audit trail provides a complete, tamper-proof record of all actions taken in the system. Essential for compliance and security reviews.",
    sections: [
      {
        title: "What's Logged",
        content: "Every significant action is recorded:\n• User logins and logouts\n• Case views and modifications\n• Decisions made (approve, reject, etc.)\n• Messages sent to applicants\n• User management changes\n• Settings modifications\n• Evidence uploads and deletions"
      },
      {
        title: "Filtering the Log",
        content: "Use filters to find specific events:\n• **Date Range**: Narrow to specific time period\n• **User**: See actions by a specific team member\n• **Action Type**: Filter by login, decision, etc.\n• **Resource**: Find events related to a specific case"
      },
      {
        title: "Log Entry Details",
        content: "Each entry shows:\n• **Timestamp**: Exact time of action\n• **User**: Who performed it\n• **Action**: What was done\n• **Resource**: Which case or entity\n• **IP Address**: Where the action originated\n• **Details**: Additional context"
      },
      {
        title: "Compliance Use",
        content: "Use the audit trail to:\n• Investigate suspicious activity\n• Prove compliance during audits\n• Track who accessed sensitive data\n• Reconstruct case handling history"
      }
    ],
    tips: [
      "Export audit logs regularly for backup",
      "Review unusual login patterns periodically",
      "Use date filters to investigate specific incidents"
    ]
  },
  
  settings: {
    title: "System Settings",
    description: "Configure global system settings that affect how EdoVerify operates. Changes here impact all users.",
    sections: [
      {
        title: "General Settings",
        content: "Basic configuration options:\n• **Company Name**: Displayed in emails and UI\n• **Default SLA Hours**: Time limit for case completion\n• **Auto-Assignment**: Automatically assign cases to agents\n• **Notification Preferences**: Enable/disable email notifications"
      },
      {
        title: "Verification Settings",
        content: "Control the verification process:\n• **Required Documents**: What documents to request\n• **Supported Countries**: Enable/disable country options\n• **Risk Thresholds**: Scores for auto-approval/rejection"
      },
      {
        title: "Security Settings",
        content: "Security-related configuration:\n• **Session Timeout**: How long before auto-logout\n• **Password Policy**: Minimum requirements\n• **Two-Factor Authentication**: Enable 2FA requirement"
      }
    ],
    tips: [
      "Test settings changes in a staging environment first",
      "Document any changes for team awareness",
      "Review settings periodically for optimization"
    ]
  },
  
  integrations: {
    title: "Integration Settings",
    description: "Configure third-party services that power EdoVerify's features. API keys and credentials are stored securely.",
    sections: [
      {
        title: "Email (Resend)",
        content: "Configure email delivery:\n• **API Key**: Your Resend API key\n• **From Email**: Sender address for system emails\n\nRequired for sending invitations, notifications, and messages."
      },
      {
        title: "SMS (Twilio)",
        content: "Configure SMS notifications:\n• **Account SID**: Twilio account identifier\n• **Auth Token**: Twilio authentication token\n• **Phone Number**: Your Twilio phone number\n\nUsed for sending SMS to applicants."
      },
      {
        title: "AI Analysis (OpenAI)",
        content: "Configure AI-powered document analysis:\n• **API Key**: Your OpenAI API key\n\nPowers face matching, document validation, and fraud detection."
      },
      {
        title: "Storage (AWS S3)",
        content: "Configure document storage:\n• **Access Key**: AWS access key ID\n• **Secret Key**: AWS secret access key\n• **Bucket Name**: S3 bucket for evidence storage\n\nIf not configured, documents are stored in the database."
      },
      {
        title: "Environment vs Database",
        content: "Settings can come from:\n• **Environment Variables**: Set in server config (take priority)\n• **Database Settings**: Configured here in the UI\n\nEnvironment variables are recommended for production."
      }
    ],
    tips: [
      "Use environment variables for sensitive credentials in production",
      "Test integrations after configuration changes",
      "Keep API keys secure and rotate them periodically"
    ]
  },
  
  profile: {
    title: "Your Profile",
    description: "Manage your personal account settings, update your information, and change your password.",
    sections: [
      {
        title: "Personal Information",
        content: "Update your profile details:\n• **Display Name**: How your name appears in the system\n• **Email**: Your login email (may require verification)\n• **Phone**: Optional contact number"
      },
      {
        title: "Security",
        content: "Manage your account security:\n• **Change Password**: Update your login password\n• **Active Sessions**: View and manage logged-in devices\n• **Two-Factor Auth**: Enable additional security"
      }
    ],
    tips: [
      "Use a strong, unique password",
      "Enable two-factor authentication for extra security",
      "Log out from devices you no longer use"
    ]
  }
};

export function PageHelp({ pageKey }) {
  const [open, setOpen] = useState(false);
  const explanation = pageExplanations[pageKey];
  
  if (!explanation) {
    return null;
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="gap-2"
        data-testid="page-help-btn"
      >
        <HelpCircle className="h-4 w-4" />
        Page Help
      </Button>
      
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <BookOpen className="h-5 w-5 text-emerald-600" />
              {explanation.title}
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="max-h-[65vh] pr-4">
            <div className="space-y-6">
              {/* Description */}
              <p className="text-muted-foreground leading-relaxed">
                {explanation.description}
              </p>
              
              <Separator />
              
              {/* Sections */}
              {explanation.sections.map((section, index) => (
                <div key={index} className="space-y-2">
                  <h3 className="font-semibold text-base flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-emerald-600" />
                    {section.title}
                  </h3>
                  <div className="text-sm text-muted-foreground pl-6 whitespace-pre-line leading-relaxed">
                    {section.content.split('**').map((part, i) => 
                      i % 2 === 1 ? <strong key={i} className="text-foreground">{part}</strong> : part
                    )}
                  </div>
                </div>
              ))}
              
              {/* Tips */}
              {explanation.tips && explanation.tips.length > 0 && (
                <>
                  <Separator />
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h3 className="font-semibold text-base flex items-center gap-2 text-amber-800 mb-3">
                      <Lightbulb className="h-4 w-4" />
                      Pro Tips
                    </h3>
                    <ul className="space-y-2">
                      {explanation.tips.map((tip, index) => (
                        <li key={index} className="text-sm text-amber-900 flex items-start gap-2">
                          <span className="text-amber-600 mt-1">•</span>
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PageHelp;
